'use client';

import { forwardRef, useMemo, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import {
  Card,
  SearchInput,
  Chip,
  ChipRow,
  FilterBar,
  selectValue,
  type FilterGroup,
  type FilterValue,
} from '@/components/ui';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatTime } from '@/lib/utils';
import { useNameFormatting } from '@/contexts/NameFormattingContext';
import { useDisplayTimeZone } from '@/contexts/DisplayTimeZoneContext';
import { useNowMs } from '@/hooks/useNowMs';
import { courtLabel, isMultiVenue, type EventVenue } from '../../libs/venues';
import { courtDefinitionsOf } from '../../libs/venuesLoad';
import { defaultMatchDay, formatDayKey, todayKeyInZone } from '../../libs/matchesDay';
import {
  buildMatchRow,
  dayKeysOf,
  divisionsOf,
  UNKNOWN_MATCH_VENUE,
  type MatchFilterStatus,
  groupByDay,
  groupByStart,
  groupByVenue,
  sortRows,
} from '../../libs/eventMatchesView';
import { matchScope, applyMatchFacets, matchFacetCounts } from '../../libs/eventMatchesFilters';
import { VENUE_DOT_CLASSES, venueColorIndex } from '../(tabs)/schedule/libs/venueColumns';
import { nowRowIndex } from '../(tabs)/schedule/libs/slotGrid';
import { RefreshHeader } from '../(tabs)/schedule/components/shared/RefreshHeader';
import { useMatchFilterUrl } from './useMatchFilterUrl';
import { useEventFollows } from './EventFollowProvider';
import { EventFollowStatus } from './EventFollowStatus';
import { JourneyShareButton } from './JourneyShareButton';
import { EventMatchRow } from './EventMatchRow';
import { useEventMatches } from './useEventMatches';
import { useEventLiveScores } from './useEventLiveScores';
import { applyMatchRowScore, isEventActiveOnDay } from '../../libs/liveScoreViews';
import type { Database } from '@/types/supabase';
import { Skeleton } from '@/components/ui/Skeleton';

interface EventMatchesProps {
  eventId: string;
  slug?: string;
  locale: string;
  /** Its dates and court list (`settings.matchScheduler.courtDefinitions`) are read. */
  event: Pick<Database['public']['Tables']['event']['Row'], 'start_date' | 'end_date'> & {
    settings: unknown;
  };
  ratingsEnabled?: boolean;
  venues: EventVenue[];
}

export function EventMatches({
  eventId,
  slug,
  locale,
  event,
  ratingsEnabled = true,
  venues,
}: EventMatchesProps) {
  const t = useTranslations('Event.matches');
  const tMatches = useTranslations('Matches');
  const tSchedule = useTranslations('PublicSchedule');
  const tLive = useTranslations('AdminMissionControl.liveSchedule');
  const { isJapanese } = useNameFormatting();

  // Day buckets follow the event's pinned zone, else the viewer's clock. Only
  // read once rows exist (after the post-mount fetch), so it is hydration-safe.
  const pinnedZone = useDisplayTimeZone();
  const dayZone = pinnedZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data, loading, error, updatedAt, reload } = useEventMatches(eventId);
  const nowMs = useNowMs(30_000);
  const today = nowMs === null ? null : todayKeyInZone(nowMs, dayZone);

  const { state: url, update: updateUrl } = useMatchFilterUrl();
  const follows = useEventFollows();
  const tFollow = useTranslations('Event.follow');
  // Where following is not offered (entrants hidden from this viewer) a shared Following link lists
  // every match: there is nothing of theirs to narrow by.
  const following = url.following && !follows.unavailable;
  const pickedDay = url.day;
  const query = url.query;
  const setQuery = (query: string) => updateUrl({ query });
  const filters: FilterValue = {
    following: following ? '1' : undefined,
    division: url.division || undefined,
    venue: url.venue || undefined,
    status: url.status || undefined,
  };
  const setFilters = (next: FilterValue) =>
    updateUrl({
      following: selectValue(next, 'following') === '1',
      division: selectValue(next, 'division') ?? '',
      venue: selectValue(next, 'venue') ?? '',
      status: (selectValue(next, 'status') ?? '') as typeof url.status,
    });
  const searching = query.trim() !== '';
  const live = useEventLiveScores(
    eventId,
    isEventActiveOnDay(event, today, dayZone) &&
      (searching || pickedDay === null || pickedDay === today),
  );
  // Asks at the first nonzero live topology and at every change; `useEventMatches`
  // drops an ask its rows already reflect (the payload reports its own revision),
  // so a visit costs one matches request unless the structure really moved.
  const topology = useRef<number | null>(null);
  useEffect(() => {
    if (live.updatedAt === null) return;
    const previous = topology.current;
    topology.current = live.topologyRevision;
    if (
      (previous === null && live.topologyRevision > 0) ||
      (previous !== null && previous !== live.topologyRevision)
    )
      void reload(live.topologyRevision);
  }, [live.topologyRevision, live.updatedAt, reload]);

  const venueCtx = useMemo(
    () => ({ venues, courts: courtDefinitionsOf(event.settings) }),
    [venues, event.settings],
  );
  const luckyLoserLabel = tLive('luckyLoser');
  const rows = useMemo(
    () =>
      sortRows(
        (data?.matches ?? []).map((m) =>
          applyMatchRowScore(
            buildMatchRow(m, {
              isJapanese,
              luckyLoserLabel,
              luckyLoserPlayerIdsByGroup: data?.luckyLoserPlayerIdsByGroup ?? {},
              teamEntries: data?.teamEntries ?? {},
              courtLabel: (id) => courtLabel(venueCtx, id),
              venueContext: venueCtx,
            }),
            live.byScheduleId[m.id],
            [m.player_1?.id ?? null, m.player_2?.id ?? null],
          ),
        ),
      ),
    [data, isJapanese, luckyLoserLabel, venueCtx, live.byScheduleId],
  );

  const divisions = useMemo(() => divisionsOf(rows), [rows]);
  const days = useMemo(() => dayKeysOf(rows, dayZone), [rows, dayZone]);

  const defaultDay = defaultMatchDay(days, today);
  const selectedDay = pickedDay && days.includes(pickedDay) ? pickedDay : defaultDay;
  const viewingToday = !searching && today !== null && selectedDay === today;

  const facets = useMemo(
    () => ({
      divisionId: selectValue(filters, 'division'),
      venueId: selectValue(filters, 'venue'),
      status: selectValue(filters, 'status') as MatchFilterStatus | undefined,
    }),
    [filters],
  );
  const followedRows = useMemo(
    () =>
      following
        ? rows.filter((row) =>
            row.sides.some((side) => side.playerId && follows.playerIds.has(side.playerId)),
          )
        : rows,
    [rows, following, follows.playerIds],
  );
  const scope = useMemo(
    () => matchScope(followedRows, { day: selectedDay, zone: dayZone, query }),
    [followedRows, selectedDay, dayZone, query],
  );
  const visible = useMemo(() => applyMatchFacets(scope, facets), [scope, facets]);
  const counts = useMemo(() => matchFacetCounts(scope, facets), [scope, facets]);
  const statuses = useMemo(
    () =>
      (['upcoming', 'live', 'finished'] as const).filter(
        (status) =>
          // Live updates can empty a status without invalidating the user's selection.
          status === facets.status ||
          rows.some((row) => (row.state === 'winnerless' ? 'finished' : row.state) === status),
      ),
    [rows, facets.status],
  );
  const venueOptions = useMemo(
    () => [
      ...venues.map((venue) => ({
        value: venue.id,
        label: venue.name,
        adornment: (
          <span
            aria-hidden="true"
            className={`size-1.5 shrink-0 rounded-full ${VENUE_DOT_CLASSES[venueColorIndex(venues, venue.id)!]}`}
          />
        ),
      })),
      ...(rows.some((row) => row.venueId === null)
        ? [{ value: UNKNOWN_MATCH_VENUE, label: t('unknownVenue') }]
        : []),
    ],
    [venues, rows, t],
  );
  const groups = useMemo<FilterGroup[]>(
    () => [
      ...(follows.unavailable
        ? []
        : [
            {
              kind: 'select' as const,
              id: 'following',
              label: tFollow('players'),
              allLabel: tFollow('allPlayers'),
              showWhenSingle: true,
              options: [{ value: '1', label: tFollow('following') }],
            },
          ]),
      {
        kind: 'select',
        id: 'venue',
        label: t('venueFilter'),
        allLabel: t('allVenues'),
        allCount: counts.venue.all,
        options: venueOptions.map((option) => ({
          ...option,
          count: counts.venue.options[option.value] ?? 0,
        })),
      },
      {
        kind: 'select',
        id: 'status',
        label: t('statusFilter'),
        allLabel: t('allStatuses'),
        allCount: counts.status.all,
        options: statuses.map((status) => ({
          value: status,
          label: t(`status${status[0].toUpperCase()}${status.slice(1)}`),
          count: counts.status.options[status] ?? 0,
        })),
      },
      {
        kind: 'select',
        id: 'division',
        label: t('divisionFilter'),
        allLabel: t('allDivisions'),
        allCount: counts.division.all,
        options: divisions.map((d) => ({
          value: d.id,
          label: d.name,
          count: counts.division.options[d.id] ?? 0,
        })),
      },
    ],
    [counts, venueOptions, statuses, divisions, t, tFollow, follows.unavailable],
  );
  // Remove an identity only after the complete metadata refresh confirms it no longer exists.
  useEffect(() => {
    if (!data) return;
    const patch: Partial<typeof url> = {};
    if (url.division && !divisions.some((d) => d.id === url.division)) patch.division = '';
    if (url.venue && !venueOptions.some((v) => v.value === url.venue)) patch.venue = '';
    if (Object.keys(patch).length) updateUrl(patch, true);
  }, [data, divisions, venueOptions, url.division, url.venue, updateUrl]);
  const sections = useMemo(
    () =>
      searching
        ? groupByDay(visible, dayZone)
        : selectedDay
          ? [{ day: selectedDay, rows: visible }]
          : [],
    [searching, visible, selectedDay, dayZone],
  );

  // Centre the NOW marker once per day shown, instant (a smooth scroll is
  // cancelled by any layout change that follows).
  const nowRef = useRef<HTMLDivElement | null>(null);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !viewingToday || !selectedDay || scrolledFor.current === selectedDay) return;
    if (!nowRef.current) return;
    nowRef.current.scrollIntoView({ block: 'center' });
    scrolledFor.current = selectedDay;
  });

  const pickDay = (day: string) => {
    scrolledFor.current = null;
    updateUrl({ day });
  };

  const dayLabel = (d: string) =>
    d === today ? t('todayChip', { day: formatDayKey(d, locale) }) : formatDayKey(d, locale);

  return (
    <div className="min-w-0 space-y-3">
      {loading ? (
        <Skeleton.Group>
          <Skeleton className="h-72 rounded-lg" />
        </Skeleton.Group>
      ) : rows.length === 0 ? (
        <p className="text-muted py-3 text-sm">{error && !data ? t('error') : t('noMatches')}</p>
      ) : (
        <>
          <div data-no-print className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className={searching ? 'text-muted text-xs' : 'sr-only'} aria-live="polite">
                {searching ? t('resultsCount', { count: visible.length }) : ''}
              </span>
              {days.length > 1 && !searching && (
                <ChipRow
                  label={t('dayFilter')}
                  activeKey={selectedDay ?? undefined}
                  // Fill the row and stop at the refresh button instead of bleeding under it.
                  className="mr-0 min-w-0 flex-1 pr-0 md:mr-0 md:pr-0"
                >
                  {days.map((d) => (
                    <Chip key={d} selected={d === selectedDay} onClick={() => pickDay(d)}>
                      {dayLabel(d)}
                    </Chip>
                  ))}
                </ChipRow>
              )}
              {viewingToday && updatedAt !== null && (
                <RefreshHeader
                  asOf={new Date(updatedAt).toISOString()}
                  onRefresh={() => {
                    void live.refresh();
                    void reload();
                  }}
                />
              )}
            </div>

            {error && viewingToday && (
              <p role="alert" className="text-danger text-xs">
                {t('error')}
              </p>
            )}
          </div>
          <FilterBar
            presentation="sidebar"
            groups={groups}
            value={filters}
            onChange={setFilters}
            resultCount={visible.length}
            searchActive={searching || following}
            onClearSearch={() =>
              updateUrl({ query: '', following: false, division: '', venue: '', status: '' })
            }
            labels={{
              filters: t('filters'),
              filtersApplied: (count) => t('filtersApplied', { count }),
              clearAll: t('clearAll'),
              reset: t('reset'),
              show: (count) => t('show', { count }),
              remove: (label) => t('remove', { label }),
            }}
            search={
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <JourneyShareButton
                    label={tFollow('shareView')}
                    href={
                      typeof window === 'undefined'
                        ? ''
                        : window.location.pathname + window.location.search
                    }
                  />
                </div>
                <EventFollowStatus following={following} />
                <div className="relative">
                  <SearchInput
                    value={query}
                    onChange={setQuery}
                    placeholder={t('searchPlaceholder')}
                    aria-label={t('searchLabel')}
                    className="pr-12 [&::-webkit-search-cancel-button]:hidden"
                  />
                  {searching && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label={t('clearSearch')}
                      className="text-muted hover:text-foreground absolute inset-y-0 right-0 inline-flex min-h-11 min-w-11 items-center justify-center"
                    >
                      <span aria-hidden>×</span>
                    </button>
                  )}
                </div>
              </div>
            }
          >
            <Card className="min-w-0 p-4 md:p-6 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
              <div data-no-print>
                {following &&
                (!follows.ready || follows.error || !follows.userId) ? null : visible.length ===
                  0 ? (
                  <p className="text-muted py-3 text-sm">
                    {following && follows.playerIds.size === 0
                      ? t('followingEmpty')
                      : searching
                        ? t('noResults', { query: query.trim() })
                        : t('emptyDay')}
                  </p>
                ) : (
                  sections.map((section) => {
                    const groups = groupByStart(section.rows);
                    const nowIdx =
                      nowMs === null
                        ? null
                        : nowRowIndex(groups, nowMs, { day: section.day, zone: dayZone });
                    return (
                      <section key={section.day} aria-label={formatDayKey(section.day, locale)}>
                        {searching && (
                          <h2 className="text-muted border-border border-b pt-3 pb-1 font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                            {dayLabel(section.day)}
                          </h2>
                        )}
                        {groups.map((g, i) => (
                          <div key={g.startIso} data-match-start-group={g.startIso}>
                            {nowIdx === i && (
                              <NowMarker
                                ref={nowRef}
                                label={tSchedule('now')}
                                nowMs={nowMs!}
                                locale={locale}
                              />
                            )}
                            <h3
                              data-match-group-heading
                              className={cn(
                                'border-foreground mb-2.5 flex items-baseline gap-2.5 border-b pb-2',
                                // The card's own padding is the gap at the top of the list.
                                (i > 0 || searching || nowIdx === 0) && 'pt-5',
                              )}
                            >
                              <span className="font-mono text-base font-semibold tabular-nums">
                                <LocalDateTime
                                  iso={g.startIso}
                                  locale={locale}
                                  format={formatTime}
                                />
                              </span>
                              <span className="text-muted text-xs">
                                {t('startCount', { count: g.rows.length })}
                              </span>
                            </h3>
                            {/* One venue: the event already says where, so a start is one group with no
                                band (an unassigned court must not split it into two). */}
                            {(isMultiVenue(venueCtx)
                              ? groupByVenue(g.rows)
                              : [{ venueId: null, venueName: null, rows: g.rows }]
                            ).map((venueGroup) => {
                              const color = venueColorIndex(venues, venueGroup.venueId);
                              return (
                                <div
                                  key={venueGroup.venueId ?? 'unknown'}
                                  data-match-venue-group={venueGroup.venueId ?? ''}
                                  className="@container [[data-match-venue-group]+&]:mt-3"
                                >
                                  {isMultiVenue(venueCtx) && (
                                    <h4 className="bg-surface border-border mb-2 flex items-center gap-2 rounded-md border px-3 py-1.5 text-[13px] font-semibold">
                                      {color !== null && (
                                        <span
                                          aria-hidden="true"
                                          className={`inline-block size-1.5 shrink-0 rounded-full ${VENUE_DOT_CLASSES[color]}`}
                                        />
                                      )}
                                      {venueGroup.venueName ?? t('unknownVenue')}
                                    </h4>
                                  )}
                                  <ul className="grid gap-2 @[40rem]:grid-cols-2">
                                    {venueGroup.rows.map((r) => (
                                      <EventMatchRow
                                        key={r.id}
                                        row={r}
                                        eventId={eventId}
                                        slug={slug}
                                        showRatings={ratingsEnabled}
                                        locale={locale}
                                      />
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        {nowIdx === groups.length && (
                          <NowMarker
                            ref={nowRef}
                            label={tSchedule('now')}
                            nowMs={nowMs!}
                            locale={locale}
                          />
                        )}
                      </section>
                    );
                  })
                )}
              </div>

              <div data-no-print className="flex justify-end">
                <button
                  onClick={() => window.print()}
                  className="text-muted hover:text-foreground inline-flex min-h-11 items-center gap-1 px-2 text-sm transition-colors"
                  aria-label={t('print')}
                >
                  {t('print')}
                </button>
              </div>

              {/* Print-only compact table: what is on screen, in time order. */}
              <div data-print-only>
                <p className="mb-2 text-xs font-medium">
                  {selectedDay && !searching ? formatDayKey(selectedDay, locale) : ''}
                </p>
                <table className="w-full border-collapse text-[10px]">
                  <thead>
                    <tr className="border-b border-black/20">
                      <th className="py-0.5 pr-2 text-left font-semibold">{t('columns.time')}</th>
                      <th className="py-0.5 pr-2 text-left font-semibold">{t('columns.court')}</th>
                      <th className="py-0.5 pr-2 text-left font-semibold">
                        {tMatches('columns.player1')}
                      </th>
                      <th className="py-0.5 pr-2 text-left font-semibold">
                        {tMatches('columns.player2')}
                      </th>
                      <th className="py-0.5 pr-2 text-left font-semibold">
                        {t('columns.division')}
                      </th>
                      <th className="py-0.5 text-left font-semibold">
                        {tMatches('columns.result')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.id} className="border-b border-black/5">
                        <td className="py-0.5 pr-2 whitespace-nowrap">
                          <LocalDateTime iso={r.startIso} locale={locale} format={formatTime} />
                        </td>
                        <td className="py-0.5 pr-2 whitespace-nowrap">{r.court}</td>
                        <td className="py-0.5 pr-2">{r.sides[0].name || '-'}</td>
                        <td className="py-0.5 pr-2">{r.sides[1].name || tMatches('tbd')}</td>
                        <td className="py-0.5 pr-2">{r.divisionName}</td>
                        <td className="py-0.5 whitespace-nowrap">
                          {r.state === 'winnerless'
                            ? r.outcome === 'not_played'
                              ? '—'
                              : tMatches('walkoverShort')
                            : r.games.length > 0
                              ? r.games.map((g) => `${g.p1}-${g.p2}`).join(', ')
                              : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div data-print-footer className="hidden">
                  PlayPulse
                </div>
              </div>
            </Card>
          </FilterBar>
        </>
      )}
    </div>
  );
}

const NowMarker = forwardRef<HTMLDivElement, { label: string; nowMs: number; locale: string }>(
  function NowMarker({ label, nowMs, locale }, ref) {
    return (
      <div ref={ref} aria-label={label} className="flex scroll-mt-24 items-center gap-2 py-2">
        <span className="bg-danger rounded-sm px-1.5 py-0.5 text-[0.6875rem] leading-none font-bold tracking-wider text-white uppercase">
          {label}
          {' · '}
          <LocalDateTime iso={new Date(nowMs).toISOString()} locale={locale} format={formatTime} />
        </span>
        <span className="bg-danger h-px flex-1" />
      </div>
    );
  },
);
